/* eslint-disable */
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { urls } from '@/Constants';
import { useAuth } from '@/Components/Auth/Login/auth';
import serverChatServices from '@/Services/server-chat.services';
import { expiresLabel, fmtWhen } from './chatFiles';
import styles from './ServerChatPageView.module.scss';

// Full-screen view of a live page Claude made. The HTML runs in a locked frame:
// sandbox="allow-scripts allow-downloads allow-modals" WITHOUT allow-same-origin, so it can't touch the console's login or page.
export default function ServerChatPageView() {
	const { pageId } = useParams();
	const history = useHistory();
	const { userConfig } = useAuth() || {};
	const myId = userConfig?.userDetails?.id;
	const [page, setPage] = useState(null); // {html, meta}
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [error, setError] = useState('');
	const [refreshError, setRefreshError] = useState('');
	const [deleting, setDeleting] = useState(false);
	const [deleted, setDeleted] = useState(false);

	const load = useCallback(() => {
		setLoading(true);
		setError('');
		serverChatServices.chatAction('getPage', { pageId })
			.then((res) => {
				if (res?.data?.html !== undefined) setPage(res.data);
				else setError('This page could not be loaded.');
			})
			.catch((e) => setError(String(e || 'This page could not be loaded.')))
			.finally(() => setLoading(false));
	}, [pageId]);

	useEffect(() => { load(); }, [load]);

	const refresh = () => {
		setRefreshing(true);
		setRefreshError('');
		serverChatServices.chatAction('refreshPage', { pageId })
			.then((res) => { if (res?.data?.html !== undefined) setPage(res.data); })
			.catch((e) => setRefreshError(String(e || 'Could not refresh the data.')))
			.finally(() => setRefreshing(false));
	};

	// Owner only: remove the page for everyone with the link.
	const deletePage = () => {
		if (deleting || !window.confirm('Delete this page? Anyone with the link will lose it.')) return;
		setDeleting(true);
		setRefreshError('');
		serverChatServices.chatAction('deletePage', { pageId })
			.then(() => setDeleted(true))
			.catch((e) => setRefreshError(String(e || 'Could not delete the page.')))
			.finally(() => setDeleting(false));
	};

	const meta = page?.meta || {};
	const title = meta.title || meta.name || 'Page';

	useEffect(() => {
		if (page) document.title = title;
	}, [page, title]);

	const downloadHtml = () => {
		const name = meta.name || `${title.replace(/[^A-Za-z0-9._ -]+/g, '').trim() || 'page'}.html`;
		serverChatServices.saveBlob(new Blob([page.html], { type: 'text/html' }), name);
	};

	// The server says who owns it (prod and dev user ids can differ); older servers only send ownerUid.
	const isOwner = typeof meta.isOwner === 'boolean' ? meta.isOwner
		: myId !== undefined && myId !== null && meta.ownerUid !== undefined && String(meta.ownerUid) === String(myId);
	const openChat = () => history.push(`${urls.EXTENSION}${urls.SERVER_CHAT}?chat=${encodeURIComponent(meta.chatId)}`);

	if (deleted) {
		return (
			<div className={styles.page}>
				<div className={styles.state}>
					<DeleteOutlineRoundedIcon className={styles.stateIcon} />
					<strong>Page deleted</strong>
					<span className={styles.muted}>The link no longer works for anyone.</span>
				</div>
			</div>
		);
	}

	if (loading && !page) {
		return (
			<div className={styles.page}>
				<div className={styles.state}><span className={styles.spin} /> Loading the page…</div>
			</div>
		);
	}

	if (error && !page) {
		const expired = /expired/i.test(error);
		return (
			<div className={styles.page}>
				<div className={styles.state}>
					<ErrorOutlineRoundedIcon className={styles.stateIcon} />
					<strong>{expired ? 'This page has expired' : "This page couldn't be opened"}</strong>
					<span className={styles.muted}>{expired ? 'Ask Server Chat to make it again.' : error}</span>
					{!expired && <button type='button' className={styles.btnGhost} onClick={load}>Try again</button>}
				</div>
			</div>
		);
	}

	const exp = expiresLabel(meta.expiresAt);
	return (
		<div className={styles.page}>
			<header className={styles.bar}>
				<div className={styles.info}>
					<div className={styles.title}>
						{title}
						{!!meta.version && <span className={styles.chip}>v{meta.version}</span>}
					</div>
					<div className={styles.meta}>
						{meta.ownerName ? `made by ${meta.ownerName}` : 'made in Server Chat'}
						{meta.createdAt ? `, ${fmtWhen(meta.createdAt)}` : ''}
						{exp && <span className={styles.sep}>·</span>}{exp}
						{meta.dataAsOf && <span className={styles.sep}>·</span>}
						{meta.dataAsOf ? `Data as of ${fmtWhen(meta.dataAsOf) || meta.dataAsOf}` : ''}
					</div>
				</div>
				<div className={styles.acts}>
					<button type='button' className={styles.btnGhost} onClick={refresh} disabled={refreshing} title='Re-run the saved queries and update the numbers'>
						{refreshing ? <span className={styles.spin} /> : <RefreshRoundedIcon fontSize='inherit' />} Refresh data
					</button>
					<button type='button' className={styles.btnGhost} onClick={downloadHtml}>
						<FileDownloadOutlinedIcon fontSize='inherit' /> Download HTML
					</button>
					{isOwner && !!meta.chatId && (
						<button type='button' className={styles.btnGhost} onClick={openChat}>
							<ChatBubbleOutlineRoundedIcon fontSize='inherit' /> Open chat
						</button>
					)}
					{isOwner && (
						<button type='button' className={styles.btnGhost} onClick={deletePage} disabled={deleting}>
							{deleting ? <span className={styles.spin} /> : <DeleteOutlineRoundedIcon fontSize='inherit' />} Delete page
						</button>
					)}
				</div>
			</header>
			{refreshError && <div className={styles.err}>{refreshError}</div>}
			{/* Never add allow-same-origin here: with it the page could reach the console's login. */}
			<iframe className={styles.frame} title={title} sandbox='allow-scripts allow-downloads allow-modals' srcDoc={page.html} />
		</div>
	);
}
